import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiShoppingCart, FiEye } from 'react-icons/fi';
import { toProxyUrl } from '../utils/imageUrl';

const IMG_PLACEHOLDER =
  'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="400" height="400" viewBox="0 0 400 400"%3E%3Crect width="400" height="400" fill="%23f1f5f9"/%3E%3Ctext x="50%25" y="50%25" dominant-baseline="middle" text-anchor="middle" font-family="system-ui" font-size="14" fill="%2394a3b8"%3ENo Image%3C/text%3E%3C/svg%3E';

const ProductCard = ({
  id,
  productName,
  currentPrice,
  oldPrice,
  productImageUrl,
  productHoverImageUrl,
  category,
  duration,
  rating,
  ratingsCount,
  isNew,
}) => {
  const navigate = useNavigate();

  const [mainImgOk, setMainImgOk] = useState(true);
  const [hoverImgOk, setHoverImgOk] = useState(true);
  const [mainLoaded, setMainLoaded] = useState(false);
  const [hoverLoaded, setHoverLoaded] = useState(false);

  const isOnSale = oldPrice && currentPrice < oldPrice;
  const discountPercent = isOnSale
    ? Math.round(((oldPrice - currentPrice) / oldPrice) * 100)
    : 0;

  const mainSrc = mainImgOk && productImageUrl ? toProxyUrl(productImageUrl) : IMG_PLACEHOLDER;
  const proxiedHoverUrl = toProxyUrl(productHoverImageUrl);

  const hasHoverImage =
    hoverImgOk &&
    productHoverImageUrl &&
    proxiedHoverUrl !== mainSrc;

  const handleViewDetails = () => navigate(`/product/${id}`);

  const handleAddToCart = () => {
    navigate(`/product/${id}`, { state: { openPower: true } });
  };


  return (
    <div className="group relative bg-white rounded-2xl overflow-hidden border border-sand hover:border-brand-300 shadow-card hover:shadow-card-hover transition-all duration-400 flex flex-col">

      {(isOnSale || isNew) && (
        <div className="absolute top-3 left-3 z-20 flex flex-col gap-1.5">
          {isNew && (
            <span className="bg-emerald-500 text-white text-xs font-bold py-1 px-2.5 rounded-full shadow-sm tracking-wide">
              NEW
            </span>
          )}
          {isOnSale && (
            <span className="bg-red-500 text-white text-xs font-bold py-1 px-2.5 rounded-full shadow-sm tracking-wide">
              -{discountPercent}%
            </span>
          )}
        </div>
      )}

      {/* ── Image ── */}
      <div
        className="relative aspect-square overflow-hidden bg-cream cursor-pointer"
        onClick={handleViewDetails}
      >
        {/* Shimmer placeholder shown until image loads */}
        {!mainLoaded && (
          <div className="absolute inset-0 bg-gradient-to-r from-cream via-sand to-cream animate-pulse" />
        )}

        {/* Main image */}
        <img
          src={mainSrc}
          alt={productName}
          loading="lazy"
          decoding="async"
          onLoad={() => setMainLoaded(true)}
          onError={() => { setMainImgOk(false); setMainLoaded(true); }}
          className={`w-full h-full object-cover transition-all duration-500 ${
            mainLoaded ? 'opacity-100' : 'opacity-0'
          } ${hasHoverImage ? 'group-hover:opacity-0' : 'group-hover:scale-110'}`}
        />

        {/* Hover image */}
        {hasHoverImage && (
          <img
            src={proxiedHoverUrl}
            alt={productName}
            loading="lazy"
            decoding="async"
            onLoad={() => setHoverLoaded(true)}
            onError={() => setHoverImgOk(false)}
            className={`absolute inset-0 w-full h-full object-cover opacity-0 group-hover:opacity-100 transition-opacity duration-500 ${
              hoverLoaded ? '' : 'invisible'
            }`}
          />
        )}

        {/* Hover overlay */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-end justify-center pb-4 pointer-events-none">
          <span className="bg-white/90 backdrop-blur-sm text-slate-800 text-xs font-semibold px-3 py-1.5 rounded-full shadow translate-y-2 group-hover:translate-y-0 transition-transform duration-300">
            Quick View
          </span>
        </div>
      </div>

      {/* ── Content ── */}
      <div className="p-2.5 sm:p-4 flex flex-col flex-grow">

        <div className="flex items-center justify-between mb-1.5 sm:mb-2.5 min-h-[20px]">
          {duration ? (
            <span className="text-[10px] sm:text-xs bg-cream text-brand-700 font-medium px-1.5 sm:px-2.5 py-0.5 rounded-full border border-brand-300 truncate max-w-[70%]">
              {duration}
            </span>
          ) : (
            <span />
          )}
          {rating > 0 && (
            <div className="flex items-center gap-0.5 sm:gap-1">
              <span className="text-yellow-400 text-xs leading-none">★</span>
              <span className="text-clay text-xs sm:text-sm font-semibold">
                {rating.toFixed(1)}
              </span>
            </div>
          )}
        </div>

        <h2
          className="font-display font-normal tracking-tight text-ink text-xs sm:text-[0.9375rem] leading-snug mb-1.5 sm:mb-3 line-clamp-2 cursor-pointer hover:text-brand-600 transition-colors duration-200"
          onClick={handleViewDetails}
        >
          {productName}
        </h2>

        <div className="flex flex-col sm:flex-row sm:items-baseline gap-0.5 sm:gap-2 mb-2.5 sm:mb-4 mt-auto">
          <span className="font-display font-normal text-ink text-sm sm:text-xl tracking-tight">
            EGP {Number(currentPrice).toLocaleString()}
          </span>
          {oldPrice && (
            <span className="text-clay text-[10px] sm:text-sm line-through">
              EGP {Number(oldPrice).toLocaleString()}
            </span>
          )}
        </div>

        <div className="flex gap-1.5 sm:gap-2">
          <button
            onClick={handleViewDetails}
            className="flex-1 flex items-center justify-center gap-1 py-2 sm:py-2.5 px-1.5 sm:px-3 rounded-lg sm:rounded-xl font-semibold text-[11px] sm:text-sm bg-cream hover:bg-sand text-clay border border-sand transition-all duration-200 active:scale-95"
          >
            <FiEye className="w-3 h-3 sm:w-4 sm:h-4 flex-shrink-0" />
            <span className="hidden xs:inline sm:inline">Details</span>
          </button>
          <button
            onClick={handleAddToCart}
            className="flex-1 flex items-center justify-center gap-1 py-2 sm:py-2.5 px-1.5 sm:px-3 rounded-lg sm:rounded-xl font-semibold text-[11px] sm:text-sm bg-ink text-cream hover:bg-brand-500 hover:text-ink transition-colors duration-200 hover:shadow-md active:scale-95"
          >
            <FiShoppingCart className="w-3 h-3 sm:w-4 sm:h-4 flex-shrink-0" />
            <span>Select</span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default ProductCard;
